import type { ApsPrefillTagPreview } from '@common/types/quoteTemplates';
import type { TemplateFormField } from '@common/types/templateFormFields';
import { queryOptions, useQuery } from '@tanstack/react-query';
import {
    fetchApsPrefillTags,
    fetchQuoteTemplateAttachments,
    fetchQuoteTemplateById,
    fetchQuoteTemplates,
    getIntakeEmailReminderTemplate,
    getIntakeEmailTemplate,
    getQuoteEmailReminderTemplate,
} from '@/services/QuoteTemplatesService';
import { getQuoteTemplateForms } from '@/services/TemplateFormsService';

// Per-template keys sit under ['templates', quoteTemplateId], so invalidating ['templates'] after
// saving a template also refreshes them

export function quoteTemplateQueryOptions(quoteTemplateId?: number | null) {
    return queryOptions({
        queryKey: ['templates', quoteTemplateId],
        queryFn: async () => {
            const response = await fetchQuoteTemplateById(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function quoteTemplateAttachmentsQueryOptions(quoteTemplateId?: number | null) {
    return queryOptions({
        queryKey: ['templates', quoteTemplateId, 'attachments'],
        queryFn: async () => {
            const response = await fetchQuoteTemplateAttachments(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function intakeEmailTemplateQueryOptions(quoteTemplateId?: number | null) {
    return queryOptions({
        queryKey: ['templates', quoteTemplateId, 'intake-email'],
        queryFn: async () => {
            const response = await getIntakeEmailTemplate(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function intakeEmailReminderTemplateQueryOptions(quoteTemplateId?: number | null) {
    return queryOptions({
        queryKey: ['templates', quoteTemplateId, 'intake-email-reminder'],
        queryFn: async () => {
            const response = await getIntakeEmailReminderTemplate(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function quoteEmailReminderTemplateQueryOptions(quoteTemplateId?: number | null) {
    return queryOptions({
        queryKey: ['templates', quoteTemplateId, 'quote-email-reminder'],
        queryFn: async () => {
            const response = await getQuoteEmailReminderTemplate(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function quoteTemplateFormFieldsQueryKey(
    quoteTemplateId?: number | string | null,
    includeDeletedTemplateFields = false,
) {
    return ['quote-template-form-fields', quoteTemplateId, includeDeletedTemplateFields] as const;
}

export function apsPrefillTagsQueryKey(quoteTemplateId: number) {
    return ['aps-prefill-tags', quoteTemplateId] as const;
}

export function useApsPrefillTags(quoteTemplateId: number | null) {
    return useQuery<ApsPrefillTagPreview>({
        queryKey: apsPrefillTagsQueryKey(quoteTemplateId ?? 0),
        queryFn: async () => {
            const response = await fetchApsPrefillTags(quoteTemplateId as number);
            return response.data;
        },
        enabled: !!quoteTemplateId,
    });
}

export function useQuoteTemplateFormFields(
    quoteTemplateId?: number | string | null,
    includeDeletedTemplateFields = false,
) {
    return useQuery<TemplateFormField[]>({
        queryKey: quoteTemplateFormFieldsQueryKey(quoteTemplateId, includeDeletedTemplateFields),
        queryFn: async () => {
            const quoteTemplateResp = await getQuoteTemplateForms(
                quoteTemplateId as number,
                includeDeletedTemplateFields,
            );
            return quoteTemplateResp.data.templateFormFields ?? [];
        },
        enabled: !!quoteTemplateId,
    });
}

export function useTemplates(lawFirmId?: number) {
    return useQuery({
        queryKey: ['templates', { lawFirmId: lawFirmId }],
        queryFn: async () => {
            if (!lawFirmId) return [];
            const response = await fetchQuoteTemplates(lawFirmId);
            return response.data;
        },
        enabled: !!lawFirmId,
        staleTime: 5 * 60_000,
    });
}
